import React from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Divider,
  Breadcrumbs,
  Link,
  Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const RefundPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link component={RouterLink} to="/" color="inherit">
          Home
        </Link>
        <Typography color="text.primary">Refund Policy</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Refund Policy
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Last Updated: March 7, 2025
        </Typography>
      </Box>
      
      {/* Content */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          borderRadius: 4
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          Our Refund Policy
        </Typography>
        <Typography variant="body1" paragraph>
          At SharkPanzer, we want you to be completely satisfied with your subscription. SharkPanzer is a product of UnwiringTech®. Unwiring Tech GSTIN- 09CYNPA1063Q1ZZ. We understand that sometimes a service may not meet your expectations, and we're committed to handling refund requests fairly and transparently.
        </Typography>
        <Typography variant="body1" paragraph>
          We pride ourselves on our strong ethics and moral values. If you encounter any issues with our service, please contact our support team at support@unwiringtech.com, and we will do our best to resolve your concerns. We are committed to providing the highest quality service and ensuring customer satisfaction.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          1. Eligibility for Refunds
        </Typography>
        <Typography variant="body1" paragraph>
          We offer a 3-day money-back guarantee for all paid subscription plans. If you are not satisfied with our service for any reason, you can request a full refund within 3 days of your initial purchase, provided you have used less than 10% of your subscription credits.
        </Typography>
        <Typography variant="body1" paragraph>
          To be eligible for a refund:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Your refund request must be made within 3 days of the initial purchase date
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              The request must be for your first subscription payment (renewal payments are not eligible for automatic refunds)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Your account must be in good standing and not in violation of our Terms of Service
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              You must have used less than 10% of your subscription credits (10% is sufficient to evaluate the quality of our service)
            </Typography>
          </li>
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          2. Refund Process
        </Typography>
        <Typography variant="body1" paragraph>
          To request a refund:
        </Typography>
        <Typography component="ol" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Contact our support team at support@unwiringtech.com with the subject line "Refund Request"
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Include your account email address and the reason for your refund request
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Our team will review your request and respond within 2 business days
            </Typography>
          </li>
        </Typography>
        <Typography variant="body1" paragraph>
          If your refund request is approved:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              The refund will be processed through your original payment method
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Depending on your payment provider, it may take 5-10 business days for the refund to appear in your account
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Your subscription will be canceled, and you will lose access to premium features
            </Typography>
          </li>
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          3. Exceptions and Special Circumstances
        </Typography>
        <Typography variant="body1" paragraph>
          After the 3-day period, refunds are generally not provided. However, we understand that exceptional circumstances may arise. In such cases, refunds may be considered on a case-by-case basis for exceptional circumstances.
        </Typography>
        <Typography variant="body1" paragraph>
          Exceptional circumstances may include:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Extended service outages or technical issues that significantly impact your ability to use our service
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Billing errors or duplicate charges
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Other circumstances as determined by our support team
            </Typography>
          </li>
        </Typography>
        <Typography variant="body1" paragraph>
          For refund requests based on exceptional circumstances, please contact our support team with detailed information about your situation. Each request will be reviewed on a case-by-case basis.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          4. Non-Refundable Items
        </Typography>
        <Typography variant="body1" paragraph>
          The following are not eligible for refunds:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Subscription renewal payments (after the initial payment)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
            Refunds will not be provided for accounts found in violation of our Terms of Service to maintain fairness and integrity of our platform
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Partial refunds for unused portions of a subscription period (except in exceptional circumstances)
            </Typography>
          </li>
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          5. Cancellation Policy
        </Typography>
        <Typography variant="body1" paragraph>
          You can cancel your subscription at any time through your account settings. If you cancel your subscription:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              You will continue to have access to your paid features until the end of your current billing cycle
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Your subscription will not automatically renew for the next billing cycle
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
            No refund will be provided for the current billing cycle (unless within the 3-day money-back guarantee period).
            </Typography>
          </li>
        </Typography>
        <Typography variant="body1" paragraph>
          Canceling your subscription does not automatically trigger a refund. If you wish to request a refund, please follow the refund process outlined above.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          6. Changes to This Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting on our website. We encourage you to review this policy periodically to stay informed of any updates.
        </Typography>
        <Typography variant="body1" paragraph>
          If we make material changes to this policy, we will notify you through our website or via email.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          7. Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have any questions about our Refund Policy, please contact us at:
        </Typography>
        <Typography variant="body1" paragraph>
          Email: support@unwiringtech.com
        </Typography>
        
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Need help with a refund or have questions?
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/contact"
            sx={{ 
              mt: 2,
              borderRadius: 4,
              px: 4,
              py: 1.5
            }}
          >
            Contact Support
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default RefundPage;
